import * as React from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import AuthRoute from "./AuthRoute";
import InternalRoute from "./InternalRoute";
import Verification from "../pages/authentication/verification";
import MetricsDashboard from "../pages/metricsDashboard";
import { AuthProvider } from "../context/AuthContext";
import RestrictedRoute from "./RestrictedRoute";
import AdminLogin from "../pages/authentication/adminLogin";
import AdminDashboard from "../pages/admin";
import ManagementDashboard from "../pages/management";
import ApplicationErrorDetails from "../pages/admin/applicationErrorDetails";
import ApplicationErrors from "../pages/admin/tiles/applicationErrors";

// Lazy-loaded components
const Layout = React.lazy(() => import("../components/Layout/layout"));
const ConciergeLogin = React.lazy(() => import("../pages/authentication/conciergeLogin"));
const ManagementLogin = React.lazy(() => import("../pages/authentication/managementLogin"));
const CustomerLogin = React.lazy(() => import("../pages/authentication/customerLogin"));
const Login = React.lazy(() => import("../pages/authentication"));
const Dashboard = React.lazy(() => import("../pages/dashboard"));
const RecommendedDoctors = React.lazy(() => import("../pages/recommendedDoctors"));
const SearchPage = React.lazy(() => import("../pages/search"));
const DoctorDetails = React.lazy(() => import("../pages/doctorDetails"));
const ChatBot = React.lazy(() => import("../pages/chatbot"));
const UploadPage = React.lazy(() => import("../pages/upload"));
const AuthCallback = React.lazy(() => import("../pages/authentication/authCallback"));
const SamlSetupPage = React.lazy(() => import("../pages/saml"));
const UserTest = React.lazy(() => import("../pages/authentication/userTestLogin"));

interface PreserveQueryNavigateProps {
    to: string;
}

// ✅ Preserves query params when navigating
const PreserveQueryNavigate = ({ to }: PreserveQueryNavigateProps): React.ReactElement => {
    const location = useLocation();
    const search = location.search;
    return <Navigate to={`${to}${search}`} replace />;
};

export const routes = createBrowserRouter([
    {
        path: "/",
        element: (
            <AuthProvider>
                <Layout />
            </AuthProvider>
        ),
        children: [
            {
                index: true,
                element: <PreserveQueryNavigate to="login" />,
            },
            {
                path: "login",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <Login />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "test_user_access",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <UserTest />
                    </React.Suspense>
                ),
            },
            {
                path: "concierge_login",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <ConciergeLogin />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "management_login",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <ManagementLogin />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "admin_login",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <AdminLogin />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "customer_login",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <CustomerLogin />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "auth-callback",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthCallback />
                    </React.Suspense>
                ),
            },
            {
                path: "verification",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <AuthRoute>
                            <Verification />
                        </AuthRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "dashboard",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                            <Dashboard />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "recommended-doctors",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                            <RecommendedDoctors />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "search",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                            <SearchPage />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "doctor-details",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                            <DoctorDetails />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "chat",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                            <ChatBot />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "metrics-dashboard",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <InternalRoute>
                                <MetricsDashboard />
                            </InternalRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "upload",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                                <UploadPage />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "admin-dashboard",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                                <AdminDashboard />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "management-dashboard",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                                <ManagementDashboard />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "saml-setup",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                                <SamlSetupPage />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "admin/application-errors",
                element: (
                    <React.Suspense fallback={<>Please Wait...</>}>
                        <PrivateRoute>
                            <RestrictedRoute>
                                <ApplicationErrors />
                            </RestrictedRoute>
                        </PrivateRoute>
                    </React.Suspense>
                ),
            },
            {
                path: "admin/application-errors/:errorId",
                element: <ApplicationErrorDetails />,
            },
        ],
    },
    {
        path: "*",
        element: <PreserveQueryNavigate to="login" />,
    },
]);
